import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAllTickets } from '../api/services/ticketsService';
import { getAllVerifications } from '../api/services/verificationsService';
import { canOpenSection, isStaff } from '../lib/permissions';

export interface PendingItem {
  id: string;
  kind: 'ticket' | 'verification';
  title: string;
  subtitle: string;
  date?: string;
  /** Route à ouvrir au clic, avec le paramètre qui désigne l'élément visé. */
  target: string;
}

/**
 * Ce qui attend une action du personnel : tickets ouverts et documents à
 * vérifier, rassemblés en une seule liste.
 *
 * Partagé par la cloche et par la page « À traiter » — deux vues du même
 * contenu, qui doivent compter et ordonner de la même façon. Les sections
 * fermées au rôle sont écartées : proposer un lien vers un écran interdit ne
 * rendrait service à personne.
 */
export function usePendingItems() {
  const { user, token } = useAuth();
  const [items, setItems] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(true);

  const staff = isStaff(user?.role);
  const role = user?.role;

  useEffect(() => {
    if (!staff || !token) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all([
      canOpenSection(role, 'support') ? getAllTickets(token) : Promise.resolve([]),
      canOpenSection(role, 'verifications') ? getAllVerifications(token) : Promise.resolve([])
    ])
      .then(([tickets, verifications]) => {
        if (cancelled) return;

        const ticketItems: PendingItem[] = (Array.isArray(tickets) ? tickets : [])
          .filter((t: any) => t.status === 'open')
          .map((t: any) => ({
            id: t.id_ticket,
            kind: 'ticket' as const,
            title: t.subject || 'Ticket sans objet',
            subtitle: [t.user_first_name, t.user_last_name].filter(Boolean).join(' ') || 'Utilisateur',
            date: t.created_at,
            target: `/support?ticket=${t.id_ticket}`
          }));

        const verificationItems: PendingItem[] = (Array.isArray(verifications) ? verifications : [])
          .filter((v: any) => v.status === 'pending')
          .map((v: any) => ({
            id: v.id_verification,
            kind: 'verification' as const,
            title: v.type === 'license' ? 'Permis à vérifier' : 'Document à vérifier',
            subtitle: v.driver_license?.license_number
              ? `N° ${v.driver_license.license_number}`
              : 'Déposé par un utilisateur',
            date: v.created_at,
            target: `/verifications?focus=${v.id_verification}`
          }));

        // Le plus ancien d'abord : c'est celui qui attend depuis trop longtemps.
        setItems(
          [...ticketItems, ...verificationItems].sort(
            (a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime()
          )
        );
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [staff, token, role]);

  return { items, loading };
}

/**
 * Depuis quand, en toutes lettres. Une date absolue oblige à calculer ; ce
 * qu'on veut savoir d'une file d'attente, c'est ce qui traîne.
 */
export function relativeTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'hier';
  if (days < 30) return `il y a ${days} j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}
